import { useCallback } from 'react';
import { transform } from 'ol/proj';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';

/**
 * Custom hook for map zoom operations
 * Handles coordinate transformation and map view operations
 */
export const useMapZoom = () => {
  const { map, displayProjection, dataProjection } = useOpenLayersContext();

  /**
   * Zoom to specific coordinates on the map
   * @param coordinates - [longitude, latitude] in EPSG:4326 format
   * @param options - Optional zoom configuration
   */
  const zoomToCoordinates = useCallback(
    (
      coordinates: [number, number],
      options: {
        pointBuffer?: number;
        extentBuffer?: number;
        maxZoom?: number;
      } = {}
    ) => {
      if (!map) {
        console.warn('Map not available for zoom operation');
        return;
      }

      const {
        pointBuffer = 50, // meters - controls zoom level
        extentBuffer = 100, // padding around the extent
        maxZoom = 16,
      } = options;

      const view = map.getView();

      try {
        // Transform from data projection (typically WGS84) to display projection
        const transformedCoords = transform(
          coordinates,
          dataProjection,
          displayProjection
        );

        // Create a small extent around the point
        const extent = [
          transformedCoords[0] - pointBuffer,
          transformedCoords[1] - pointBuffer,
          transformedCoords[0] + pointBuffer,
          transformedCoords[1] + pointBuffer,
        ];

        view.fit(extent, {
          padding: [extentBuffer, extentBuffer, extentBuffer, extentBuffer],
          maxZoom,
        });
      } catch (error) {
        console.error('Error zooming to coordinates:', error);
      }
    },
    [map, displayProjection, dataProjection]
  );

  return {
    zoomToCoordinates,
  };
};
